import { defineStore } from 'pinia'
import {computed, ref} from "vue";
import Siren from '@/Utils/Siren';

export const useUserStore = defineStore('user', () => {
    const userId = ref(null) // Initialize userId from localStorage
    const subscription = ref(null) // Initialize subscription from localStorage
    const adaptiveAudioMinutes = ref(null) // Initialize adaptive audio minutes from localStorage
    const socketTokenRequest = ref(null)
    const cloudPushUserId = ref(null)
    const alertSoundEnabled = ref(true)
    const alertSoundPreference = ref('Harp.mp3')
    const crystalState = ref(false)

    function setCloudPushUserId(id) {
        cloudPushUserId.value = id
    }
    function getCloudPushUserId() {
        return cloudPushUserId.value
    }
    function initializeUserId() {
        const id = getUserIdFromToken()
        userId.value = id
    }
    function initializeSubscription() {
        const _subscription = getSubscription()
        subscription.value = _subscription
    }
    const getHasRegistered = () => {
        return localStorage.getItem('has_registered');
    }
    const setHasRegistered = (token) =>{
        localStorage.setItem('has_registered', token);
        return token;
    }
    const getStreamChatVisited = () => {
        return localStorage.getItem('stream_chat_visited') === 'true';
    }
    const setStreamChatVisited = (visited) => {
        localStorage.setItem('stream_chat_visited', visited);
        return visited;
    }
    const hasPermission = (permission) => {
        const permissions = getPermissions();
        return permissions.includes(permission);
    }
    const setPermissions = (permissions) => {
        localStorage.setItem('permissions', JSON.stringify(permissions));
        return permissions;
    }
    function setSubscription(subscriptionData) {
        localStorage.setItem('subscription', JSON.stringify(subscriptionData));
        subscription.value = subscriptionData;
    }
    function getSubscription() {
        const storedSubscription = localStorage.getItem('subscription');
        if (storedSubscription && storedSubscription !== 'undefined') {
            const data = JSON.parse(storedSubscription);
            return data;
        }
        return null;
    }
    function hasSubscription() {
        return !!subscription.value;
    }
    function setAdaptiveAudioMinutes(minutesData) {
        localStorage.setItem('adaptive_audio_minutes', JSON.stringify(minutesData));
        adaptiveAudioMinutes.value = minutesData;
    }
    function getAdaptiveAudioMinutes() {
        const stored = localStorage.getItem('adaptive_audio_minutes');
        if (stored && stored !== 'undefined') {
            return JSON.parse(stored);
        }
        return null;
    }
    function initializeAdaptiveAudioMinutes() {
        const minutes = getAdaptiveAudioMinutes();
        adaptiveAudioMinutes.value = minutes;
    }
    function canUseAdaptiveAudio() {
        // Can use if: has subscription OR has balance > 0 OR has unlimited
        if (subscription.value) return true;
        if (!adaptiveAudioMinutes.value) return false;
        return adaptiveAudioMinutes.value.unlimited || adaptiveAudioMinutes.value.balance > 0;
    }
    const getPermissions = () => {
        const storedPermissions = localStorage.getItem('permissions');
        if (storedPermissions) {
            return JSON.parse(storedPermissions);
        }
        return [];
    }
    const getAccessToken = () => {
        return localStorage.getItem('access_token');
    }
    const setAccessToken = (token) =>{
        localStorage.setItem('access_token', token);
        userId.value = getUserIdFromToken()
        return token;
    }
    function getDecodedToken() {
        const token = getAccessToken();
        if (!token) return null

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(atob(base64));
    }
    function getUserIdFromToken() {
        const decoded = getDecodedToken();
        return decoded?.user_id;
    }
    function getGuestIdFromToken() {
        const decoded = getDecodedToken();
        return decoded?.guest_id;
    }
    function getEmailFromToken() {
        const decoded = getDecodedToken();
        return decoded?.email;
    }
    function getExpirationFromToken() {
        const decoded = getDecodedToken();
        return decoded?.exp;
    }
    function isLoggedIn() {
        const hasAccessToken = !!getAccessToken()
        if (!hasAccessToken) return false
        if (!getUserIdFromToken()) return false

        const hasValidExpiration = getExpirationFromToken() > Date.now() / 1000
        return hasValidExpiration
    }
    function isGuest() {
        const hasAccessToken = !!getAccessToken()
        if (!hasAccessToken) return false
        if (!getGuestIdFromToken()) return false

        const hasValidExpiration = getExpirationFromToken() > Date.now() / 1000
        return !hasValidExpiration
    }
    function setSocketTokenRequest(tokenParams) {
        socketTokenRequest.value = tokenParams;
        localStorage.setItem('socket_token_request', JSON.stringify(tokenParams));
    }
    function getSocketTokenRequest() {
        if (socketTokenRequest.value) {
            return socketTokenRequest.value;
        }
        return JSON.parse(localStorage.getItem('socket_token_request'));
    }
    // Alert Sound Preferences
    const getAlertSoundEnabled = () => {
        const stored = localStorage.getItem('alert_sound_enabled');
        return stored !== null ? stored === 'true' : true;
    }

    const getAlertSoundPreference = () => {
        return localStorage.getItem('alert_sound_preference') || 'Harp.mp3';
    }

    const setAlertSoundEnabled = (enabled) => {
        localStorage.setItem('alert_sound_enabled', enabled.toString());
        alertSoundEnabled.value = enabled;
        Siren.setPreferences(enabled, alertSoundPreference.value);
    }

    const setAlertSoundPreference = (sound) => {
        localStorage.setItem('alert_sound_preference', sound);
        alertSoundPreference.value = sound;
        Siren.setPreferences(alertSoundEnabled.value, sound);
    }

    const initializeAlertSoundPreferences = (settingsResponse) => {
        if (settingsResponse?.settings) {
            setAlertSoundEnabled(settingsResponse.settings.alert_sound_enabled);
            setAlertSoundPreference(settingsResponse.settings.alert_sound_preference);
        } else {
            alertSoundEnabled.value = getAlertSoundEnabled();
            alertSoundPreference.value = getAlertSoundPreference();
        }
        Siren.init(alertSoundEnabled.value, alertSoundPreference.value);
    }

    // Crystal State
    const getCrystalState = () => {
        const stored = localStorage.getItem('crystal_state');
        return stored !== null ? stored === 'true' : false;
    }

    const setCrystalState = (state) => {
        localStorage.setItem('crystal_state', state.toString());
        crystalState.value = state;
    }

    const initializeCrystalState = (settingsResponse) => {
        if (settingsResponse?.settings) {
            crystalState.value = settingsResponse.settings.crystal_state;
        } else {
            crystalState.value = getCrystalState();
        }
    }

    function clearUserStore() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('socket_token_request');
        localStorage.removeItem('permissions');
        localStorage.removeItem('subscription');
        localStorage.removeItem('adaptive_audio_minutes');
        localStorage.removeItem('alert_sound_enabled');
        localStorage.removeItem('alert_sound_preference');
        localStorage.removeItem('crystal_state');
        socketTokenRequest.value = null
        userId.value = null
        subscription.value = null
        adaptiveAudioMinutes.value = null
        alertSoundEnabled.value = true
        alertSoundPreference.value = 'Harp.mp3'
        crystalState.value = false
    }
    return {
        getCloudPushUserId,
        setCloudPushUserId,
        getAccessToken,
        setAccessToken,
        setPermissions,
        getPermissions,
        setSubscription,
        getSubscription,
        hasSubscription,
        setAdaptiveAudioMinutes,
        getAdaptiveAudioMinutes,
        initializeAdaptiveAudioMinutes,
        canUseAdaptiveAudio,
        hasPermission,
        getUserIdFromToken,
        getGuestIdFromToken,
        initializeUserId,
        initializeSubscription,
        getEmailFromToken,
        isLoggedIn,
        isGuest,
        clearUserStore,
        getSocketTokenRequest,
        setSocketTokenRequest,
        getHasRegistered,
        setHasRegistered,
        getStreamChatVisited,
        setStreamChatVisited,
        getAlertSoundEnabled,
        getAlertSoundPreference,
        setAlertSoundEnabled,
        setAlertSoundPreference,
        initializeAlertSoundPreferences,
        getCrystalState,
        setCrystalState,
        initializeCrystalState,
        userId,
        subscription,
        adaptiveAudioMinutes,
        alertSoundEnabled,
        alertSoundPreference,
        crystalState,
    };
})
